import React, { useEffect, useState } from "react";

function isCodespaceHost() {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname;
  return host.endsWith(".app.github.dev") || host.endsWith(".githubpreview.dev");
}

function currentPath() {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

function isDesignLabPath() {
  if (typeof window === "undefined") return false;
  return window.location.pathname === "/payroll" && new URLSearchParams(window.location.search).get("reference") === "1";
}

function describeClick(target) {
  const el = target?.closest?.("a,button,[role='button']");
  if (!el) return null;
  const label =
    el.getAttribute("aria-label") ||
    el.getAttribute("title") ||
    el.textContent?.trim() ||
    el.tagName;
  const href = el.getAttribute("href");
  return href ? `${label} → ${href}` : label;
}

export default function CodespaceNavigationTrace() {
  const [events, setEvents] = useState(() => (typeof window !== "undefined" ? [currentPath()] : []));
  const [lastClick, setLastClick] = useState("none yet");
  const enabled = isCodespaceHost();

  useEffect(() => {
    if (!enabled) return undefined;

    const recordPath = () => {
      const path = currentPath();
      setEvents((prev) => {
        if (prev[prev.length - 1] === path) return prev;
        return [...prev, path].slice(-6);
      });
    };

    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;
    window.history.pushState = function patchedPushState(...args) {
      const result = originalPushState.apply(this, args);
      queueMicrotask(recordPath);
      return result;
    };
    window.history.replaceState = function patchedReplaceState(...args) {
      const result = originalReplaceState.apply(this, args);
      queueMicrotask(recordPath);
      return result;
    };

    const onClick = (event) => {
      const label = describeClick(event.target);
      if (label) setLastClick(label.slice(0, 120));
    };

    window.addEventListener("popstate", recordPath);
    window.addEventListener("hashchange", recordPath);
    document.addEventListener("click", onClick, true);

    return () => {
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
      window.removeEventListener("popstate", recordPath);
      window.removeEventListener("hashchange", recordPath);
      document.removeEventListener("click", onClick, true);
    };
  }, [enabled]);

  if (!enabled || isDesignLabPath()) return null;

  const go = (path) => window.location.assign(path);

  return (
    <aside
      data-testid="codespace-navigation-trace"
      style={{
        position: "fixed",
        right: 14,
        bottom: 14,
        zIndex: 2147483000,
        width: 300,
        padding: 12,
        borderRadius: 16,
        background: "rgba(250,252,250,.94)",
        border: "1px solid rgba(54,70,59,.16)",
        boxShadow: "0 12px 36px rgba(32,45,36,.14)",
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
        color: "#28332c",
        fontFamily: "Inter,-apple-system,BlinkMacSystemFont,Helvetica Neue,Arial,sans-serif",
        fontSize: 11,
        lineHeight: 1.4,
      }}
    >
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:8,marginBottom:8}}>
        <strong style={{fontSize:11,letterSpacing:".08em",textTransform:"uppercase"}}>Navigation trace</strong>
        <span style={{color:"#66746b"}}>Codespace only</span>
      </div>
      <div style={{marginBottom:8,color:"#5f6d64"}}><b>Last click:</b> {lastClick}</div>
      <div style={{display:"grid",gap:3,marginBottom:10}}>
        {events.map((path, index) => (
          <div key={`${path}-${index}`} style={{whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",color:index===events.length-1?"#263129":"#748078"}}>
            {index === events.length - 1 ? "● " : "· "}{path}
          </div>
        ))}
      </div>
      <div style={{display:"flex",gap:6}}>
        <button type="button" onClick={() => go("/home")} style={{flex:1,border:"1px solid rgba(92,134,112,.18)",borderRadius:999,background:"rgba(92,134,112,.08)",padding:"7px 9px",cursor:"pointer",fontSize:11,color:"#405448"}}>TOTO Home</button>
        <button type="button" onClick={() => go("/payroll")} style={{flex:1,border:"1px solid rgba(92,134,112,.18)",borderRadius:999,background:"rgba(255,255,255,.76)",padding:"7px 9px",cursor:"pointer",fontSize:11,color:"#405448"}}>Payroll</button>
      </div>
    </aside>
  );
}
